/**
 * StationDashboard Component
 *
 * This component serves as the main dashboard for displaying weather station data.
 * It provides a comprehensive view of current weather conditions, historical data,
 * and station information for a specific weather station.
 *
 * Key Features:
 * - Displays current weather data using WeatherDashboard component
 * - Shows historical weather data using WeatherChart component
 * - Provides a collapsible drawer for station selection
 * - Handles station status (operational/non-operational)
 * - Implements responsive design for different screen sizes
 * - Includes error handling and loading states
 *
 * @component
 * @example
 * <StationDashboard />
 */

import React, { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import {
  Typography,
  Container,
  Skeleton,
  Box,
  Drawer,
  IconButton,
  Button,
  Backdrop,
  useMediaQuery,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PushPinIcon from "@mui/icons-material/PushPin";
import WeatherDashboard from "../WeatherDashboard/WeatherDashboard";
import { useCurrentWeatherData } from "../../hooks/useWeatherData";
import { useLastWeekWeatherData } from "../../hooks/useWeatherData";
import { useCurrentTidePredictionData } from "../../hooks/useWeatherData";
import { useAllWeatherData } from "../../hooks/useWeatherData";
import { useLunarSolarData } from "../../hooks/useWeatherData";
import WeatherChart from "../Chart/WeatherChart";
import AllStationsList from "../AllStations/AllStationsList";
import ArrowLeftIcon from "@mui/icons-material/ArrowLeft";
import DataTable from "../StationDataTable/DataTable";
import ReportIssueButton from "./ReportIssueButton.js";
import NonOperationalStation from "../WeatherDashboard/NonOperationalStation.js";
import TidePrediction from "../TidePrediction/TidePrediction.js";
import LunarSolar from "../LunarSolar/LunarSolar.js";
import { stationMetaData } from "../../utils/rawData.js";
import {
  createUrlFriendlyName,
  findStationByNameOrId,
} from "../../utils/rawFunction.js";
import NotFound from "../PageNotAFound/NotFound.js";
import ShareStationLinks from "./ShareStationLinks";
import ECCCLink from "./ECCCLink";
import StationAcknowledgement from "../StationAcknowledgement/StationAcknowledgement.js";
import SmsWeatherUpdates from "./SmsWeatherUpdates";

/**
 * Main StationDashboard component that manages the display of weather station data
 * @returns {JSX.Element} The rendered StationDashboard component
 */
function StationDashboard() {
  const { stationIdentifier } = useParams();
  const navigate = useNavigate();

  /**
   * Effect hook to handle URL-friendly station names
   * Ensures the URL always uses the correct format for station identifiers
   */
  useEffect(() => {
    const urlFriendlyName = createUrlFriendlyName(stationIdentifier);
    if (stationIdentifier !== urlFriendlyName) {
      navigate(`/${urlFriendlyName}`, { replace: true });
    }
  }, [stationIdentifier, navigate]);

  const stationId = findStationByNameOrId(stationIdentifier)?.id;

  console.log("stationIdentifier:", stationIdentifier);
  console.log("stationId", stationId);

  const { currentData, loading, error } = useCurrentWeatherData(stationId);

  const { lastWeekData, weekDataLoading, weekDataError } =
    useLastWeekWeatherData(stationId);

  const {
    tideData,
    loading: tideLoading,
    error: tideError,
  } = useCurrentTidePredictionData(stationId);

  // const {
  //   lunarSolarData,
  //   loading: lunarSolarLoading,
  //   error: lunarSolarError,
  // } = useLunarSolarData(stationId);

  const [limit, setLimit] = useState(10);
  const [offset, setOffset] = useState(0);
  {
    /* Uncomment the following lines to use the all weather data hook */
  }
  // const { allData, allDataLoading, allDataError, allDataTotalRecords } = useAllWeatherData(stationId, limit, offset);

  const [drawerOpen, setDrawerOpen] = useState(false); // close by default
  const [pinned, setPinned] = useState(false);

  // Use media query to determine screen size
  const isSmallScreen = useMediaQuery("(max-width:600px)");
  const drawerWidth = isSmallScreen ? "70%" : "350px"; // Adjust drawer size based on screen size

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const togglePin = () => {
    setPinned(!pinned);
  };
  const station = stationMetaData.find((s) => s.id === stationId);
  const isOperational = station?.status === "Operational";
  // if (!station) {
  //   return <Navigate to="/404" replace />;
  // }
  if (!stationId) {
    return <NotFound />;
  }

  /**
   * Renders a loading skeleton with optional error message
   * Used to show loading states and error messages in a consistent format
   * @param {string} errorText - Optional error message to display
   * @returns {JSX.Element} Loading skeleton or error message
   */
  const renderSkeletonWithLoader = (errorText) => (
    <Box sx={{ position: "relative", width: "100%", height: "400px" }}>
      <Skeleton variant="rectangular" width="100%" height="100%" />
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          height: "100%",
          bgcolor: "white", // Optional background for better visibility
        }}
      >
        {errorText ? (
          <Typography color="error" variant="h6">
            {errorText}
          </Typography>
        ) : (
          <CircularProgress />
        )}
      </Box>
    </Box>
  );
  return (
    <>
      <Tooltip title="Expand Menu" placement="left">
        <IconButton
          onClick={toggleDrawer}
          sx={{
            position: "fixed",
            right: 0,
            top: "50%",
            transform: "translateY(-50%)",
            width: "30px", // Adjust width for better visibility
            height: "80px", // Adjust height for better visibility
            backgroundColor: "black",
            color: "white",
            borderRadius: "5px 0 0 5px", // Rounded left side
            "&:hover": {
              backgroundColor: "black", // Optional hover effect
            },
          }}
        >
          <ArrowLeftIcon />
        </IconButton>
      </Tooltip>

      <Backdrop
        open={drawerOpen && !pinned}
        onClick={toggleDrawer}
        sx={{ zIndex: (theme) => theme.zIndex.drawer - 1 }}
      />
      <Drawer
        anchor="right"
        open={drawerOpen || pinned}
        onClose={toggleDrawer}
        variant="persistent"
        sx={{
          "& .MuiDrawer-paper": {
            width: drawerWidth,
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <IconButton onClick={toggleDrawer}>
            <ChevronRightIcon />
          </IconButton>
          <IconButton onClick={togglePin}>
            <PushPinIcon color={pinned ? "primary" : "inherit"} />
          </IconButton>
          <AllStationsList
            onStationSelect={() => {
              if (!pinned) {
                setDrawerOpen(false);
              }
            }}
          />
        </Box>
      </Drawer>
      <Container>
        {!isOperational ? (
          <NonOperationalStation stationName={station?.name || stationId} />
        ) : (
          <>
            <Container
              // maxWidth="md"
              sx={{
                mb: 4,
                transition: "margin-left 0.3s ease",
              }}
            >
              {loading ? (
                renderSkeletonWithLoader()
              ) : currentData ? (
                <>
                  <ShareStationLinks stationName={station?.name} />
                  <WeatherDashboard data={currentData} stationId={stationId} />
                </>
              ) : error ? (
                renderSkeletonWithLoader(error)
              ) : (
                renderSkeletonWithLoader("No data available.")
              )}
            </Container>

            {/* Tide Prediction Section  */}
            {/* TODO: Uncomment this when we have a way to show tide prediction */}
            <Container
              sx={{
                mb: 4,
                transition: "margin-left 0.3s ease",
              }}
            >
              <TidePrediction
                tideData={tideData}
                loading={tideLoading}
                error={tideError}
              />
            </Container>

            {/* it is under the consideration */}
            {/* Lunar Solar Section */}
            {/* {
              <Container
                sx={{
                  mb: 4,
                  transition: "margin-left 0.3s ease",
                }}
              >
                <LunarSolar
                  lunarSolarData={lunarSolarData}
                  loading={lunarSolarLoading}
                  error={lunarSolarError}
                />
              </Container>
            } */}
            <Container
              // maxWidth="md"
              sx={{
                mb: 4,
                transition: "margin-left 0.3s ease",
              }}
            >
              {weekDataLoading ? (
                renderSkeletonWithLoader()
              ) : lastWeekData ? (
                <WeatherChart weekWeatherData={lastWeekData} />
              ) : weekDataError ? (
                renderSkeletonWithLoader(weekDataError)
              ) : (
                renderSkeletonWithLoader("No data available.")
              )}
            </Container>
            {/* Station Acknowledgement Section */}
            <Container
              sx={{
                mb: 4,
                transition: "margin-left 0.3s ease",
              }}
            >
              <StationAcknowledgement stationId={stationId} />
            </Container>
          </>
        )}

        {/* <Container
					// maxWidth="md"
					sx={{
						mb: 4,
						transition: "margin-left 0.3s ease",
					}}
				>
					{allDataLoading ? (
						renderSkeletonWithLoader()
					) : allData ? (
						<DataTable
							allData={allData}
							limit={limit}
							offset={offset}
							onLimitChange={setLimit}
							onOffsetChange={setOffset}
							total_records={allDataTotalRecords}
						/>
					) : allDataError ? (
						renderSkeletonWithLoader(allDataError)
					) : (
						renderSkeletonWithLoader("No more data available.")
					)}
				</Container> */}
      </Container>
      {/* TODO: Uncomment this when we have a way to send SMS */}
      {/* <SmsWeatherUpdates /> */}
      {/* <ReportIssueButton /> */}
    </>
  );
}

export default StationDashboard;
